export const SUGGESTED_PROMPTS: {
  path: string;
  feature: string;
  overview: string;
  description: string;
  prompts: string[];
}[] = [
  {
    path: '/partners',
    feature: '[Partners] Partner List',
    overview:
      'A centralized view where users can see all organizations their company is partnered with in Crossbeam.',
    description:
      'What users do: Browse, search, and manage partner connections, quickly review data sharing status, and initiate co-selling or joint activities.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/partners/pending-requests',
    feature: '[Partners] Partner Requests',
    overview:
      'A workflow for sending, receiving, and managing invitations to connect and collaborate with new partners.',
    description:
      'What users do: Send partnership invites to new companies, accept or reject incoming requests, and onboard new partners into their ecosystem.',
    prompts: [
      'Do we have any pending partner requests I need to review?',
      'Which incoming partner requests have the most overlap with our pipeline?',
      'What’s the next step after I accept a partner request?',
    ],
  },
  {
    path: '/partners/sharing-dashboard',
    feature: '[Partners] Sharing Dashboard',
    overview:
      'An interface that summarizes what data is being shared with which partners and controls the sharing settings.',
    description:
      'What users do: Monitor and adjust data sharing settings by population or field, review overlap activity, and ensure compliance with data-sharing policies.',
    prompts: [
      'Show me a list of fields shared with all partners.',
      'Are there any recent changes to our sharing settings?',
      'Show me partners with one-way sharing only.',
    ],
  },
  {
    path: '/reports/account-mapping',
    feature: '[Mapping] Create a Report',
    overview:
      'Lets users build custom views to analyze account or lead overlaps with partners, applying filters and selecting specific data points.',
    description:
      'What users do: Define criteria and partners, filter by segments, and generate actionable overlap reports to inform co-selling, prospecting, or account planning.',
    prompts: [
      'Build a report showing my prospects with all overlapping partners.',
      'Build a report showing accounts that exist in my partner’s populations that don’t exist in my populations.',
      'What are best practices for creating a report?',
    ],
  },
  {
    path: '/reports',
    feature: '[Mapping] Saved Reports',
    overview:
      'Stores previously created reports for quick access, re-use, and sharing with teammates or stakeholders.',
    description:
      'What users do: Instantly revisit frequently used report setups, monitor evolving overlap data, and collaborate by sharing key reports internally.',
    prompts: [
      'What are my most recently viewed reports?',
      'Who has viewed or edited this saved report?',
      'Suggest reports I should be monitoring based on my role.',
    ],
  },
  {
    path: '/co-sell/requests',
    feature: '[Collaborate] Messages',
    overview:
      'Provides a communication tool within Crossbeam for users to directly contact partners about shared overlaps or co-selling opportunities.',
    description:
      'What users do: Initiate conversations around specific overlaps, request warm introductions, share intel, and track correspondence for partnership activities.',
    prompts: [
      'What should I say to a partner when kicking off a co-selling motion?',
      'Suggest an opening message to start collaborating on an overlap.',
      'What’s a good template to use when asking for an intro?',
    ],
  },
  {
    path: '/collaborate',
    feature: '[Collaborate] Shared Lists',
    overview:
      'Enables users to create and share curated lists of accounts or leads with partners, driving targeted collaboration.',
    description:
      'What users do: Build and send custom account or lead lists, focus joint activities on strategic targets, and align go-to-market outreach with partners.',
    prompts: [
      'How many closed-won deals have come from accounts in our shared lists?',
      'Which shared list has the highest engagement from partners?',
      'What’s a good note to include when sharing a list of dormant leads?',
    ],
  },
  {
    path: '/attribution',
    feature: 'Attribution',
    overview:
      'Tracks and surfaces which partners or partner activities have influenced pipeline creation or revenue outcomes within your CRM.',
    description:
      'What users do: View which deals originated or progressed due to partner involvement, justify partner value, and inform future partnership and co-selling strategy.',
    prompts: [
      'Which deals in the last quarter were influenced by partners?',
      'Are there any closed-won deals that were touched by multiple partners?',
      'Which partners should we prioritize based on attribution data?',
    ],
  },
  {
    path: '/deal-navigator',
    feature: 'Deal Navigator',
    overview:
      'A workspace to manage ecosystem-influenced deals, see partner overlap details, and take action directly from each opportunity or account.',
    description:
      'What users do: Prioritize deals with strong partner signals, review partner-provided context, connect with partner reps, and accelerate co-selling workflows.',
    prompts: [
      'Show me high-priority opps that have overlap with top-performing partners.',
      'Which open opportunities should I focus on?',
      'Who has the most open deals with partner overlap?',
    ],
  },
  {
    path: '/measure/ecosystem',
    feature: 'Performance',
    overview:
      'Provides analytics and summary metrics on partner ecosystem activity, overlap trends, and impact on pipeline and revenue.',
    description:
      'What users do: Monitor partnership program health, track pipeline influenced by partners over time, identify top-performing partners, and optimize ecosystem GTM.',
    prompts: [
      'Are we seeing an increase or decrease in partner activity this month?',
      'Summarize our ecosystem performance year to date.',
      'What’s the win rate when a partner is engaged vs not?',
    ],
  },
  {
    path: '/data-sources',
    feature: '[Data] Data Sources',
    overview:
      'Connects Crossbeam to external systems like CRMs or spreadsheets to import account, lead, and opportunity data.',
    description:
      'What users do: Link their Salesforce, HubSpot, Snowflake, CSVs, etc., so that Crossbeam stays up-to-date with their latest customer and prospect records.',
    prompts: [
      'How do I connect our Salesforce instance to Crossbeam?',
      'Can I import a list of leads from a CSV file?',
      'When was our last successful data sync?',
    ],
  },
  {
    path: '/populations',
    feature: '[Data] Populations',
    overview:
      'Customizable segments of data—such as “Customers,” “Prospects,” or “Open Opportunities”—used to control what is shared and compared with partners.',
    description:
      'What users do: Define and manage groups of accounts or leads, select which populations to include in partner overlaps, and tailor collaboration strategies.',
    prompts: [
      'Help me create a population of all current customers in North America.',
      'What criteria are we using for our ‘Open Opportunities’ population?',
      'Can I include records from multiple CRMs in one population?',
    ],
  },
  {
    path: '/default-sharing',
    feature: '[Data] Default Sharing',
    overview:
      'Pre-set rules that determine what data is automatically shared with each new partner upon connection.',
    description:
      'What users do: Configure default sharing permissions by population or data field to speed up onboarding while ensuring internal data policies are met.',
    prompts: [
      'How do I set default sharing rules for new partners?',
      'What’s currently shared by default when we connect with a new partner?',
      'Have our default settings changed in the past 30 days?',
    ],
  },
  {
    path: '/integrations',
    feature: '[Data] Integrations',
    overview:
      'Enables seamless interoperability between Crossbeam and external platforms (CRM, Slack, Zapier, etc.) to automate workflows and sync data.',
    description:
      'What users do: Activate integrations to push overlap intelligence into other tools, automate ecosystem workflows, and streamline reporting and productivity.',
    prompts: [
      'How do I connect Crossbeam to Salesforce?',
      'List all the integrations we’ve enabled so far.',
      'Can I connect Crossbeam to multiple CRMs?',
    ],
  },
];

export const DEFAULT_SUGGESTED_PROMPTS = [
  'Do I have partners with pending invites?',
  'Which partners have the highest overlap potential?',
  'Show me partners created in the last 30 days',
];
